"use client"

import {
  Box,
  Image as ChakraImage,
  Flex,
  HStack,
  Icon,
  Link,
  LinkBox,
  LinkOverlay,
  Text,
  VStack,
} from "@chakra-ui/react"

import CategoryBadgeList from "../CategoryBadgeList/CategoryBadgeList"
import { FC, useEffect, useState } from "react"

import { LocalDateProps } from "./TSearchModal"
import NextLink from "@/components/RouteTransitionLink"
import { Post } from "@/types/database"
import { ArrowUpRightOutlineIcon } from "@/components/icons"

const DEFAULT_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
}

const LocalDate: FC<LocalDateProps> = ({
  date,
  options = DEFAULT_DATE_FORMAT_OPTIONS,
}) => {
  const [formattedDate, setFormattedDate] = useState<string | null>(null)

  useEffect(() => {
    setFormattedDate(new Date(date).toLocaleDateString("en-GB", options))
  }, [date, options])

  return <time dateTime={date}>{formattedDate ?? date}</time>
}

const SearchResultCard: FC<{
  post: Post
  isHighlighted: boolean
  onClick: () => void
  index?: number
}> = ({ post, isHighlighted, onClick, index = 0 }) => {
  const { title, date_created, category, author, image, slug } = post

  return (
    <Box
      id={`search-item-${index}`}
      role="option"
      aria-selected={isHighlighted}
      tabIndex={isHighlighted ? 0 : -1}
      display="flex"
      alignItems="center"
      p={4}
      bg={isHighlighted ? "gray.100" : "transparent"}
      cursor="pointer"
      position="relative"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onClick()
        }
      }}
      color={isHighlighted ? "primary.fg" : "fg.muted"}
      bgColor={isHighlighted ? "primary.500/10" : "transparent"}
      borderWidth="1px"
      borderStyle="solid"
      borderColor={isHighlighted ? "primary.500/20" : "transparent"}
      borderBottomWidth="1px"
      borderBottomColor={isHighlighted ? "primary.500/20" : "border"}
      _hover={{
        color: "primary.fg",
        bgColor: "primary.500/10",
        _dark: {
          bgColor: "primary.300/10",
        },
      }}
    >
      {image && (
        <Flex
          position="relative"
          flexShrink={0}
          w="24"
          h="24"
          mr={4}
          display={{ base: "none", sm: "block" }}
        >
          <LinkBox w="100%" h="100%">
            <LinkOverlay asChild>
              <NextLink
                href={`/article/${slug}`}
                transitionTypes={["nav-forward"]}
              >
                <ChakraImage
                  src={
                    process.env.DIRECTUS_ASSETS_URL +
                    image.id +
                    "?quality=100&width=96"
                  }
                  alt={title}
                  objectFit="cover"
                  borderRadius="xl"
                  width="100%"
                  height="100%"
                />
              </NextLink>
            </LinkOverlay>
          </LinkBox>
        </Flex>
      )}
      <VStack flex="1" alignItems="flex-start">
        <HStack position="relative" justifyContent="space-between" w="full">
          <Link asChild>
            <NextLink
              href={`/article/${slug}`}
              transitionTypes={["nav-forward"]}
            >
              <Text fontSize="md" fontWeight="medium">
                {title}
              </Text>
            </NextLink>
          </Link>
          <Icon
            as={ArrowUpRightOutlineIcon}
            size={"md"}
            color="currenColor"
            transition={"color 0.2s ease, background-color 0.2s ease"}
          />
        </HStack>
        <HStack w="full" justifyContent="space-between">
          <VStack alignItems="flex-start" gap={0}>
            <Text fontSize="xs" color="fg.muted" mt={1}>
              <LocalDate date={date_created} />
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {author?.name + " " + author?.surname}
            </Text>
          </VStack>
          {category && category.length > 0 && (
            <CategoryBadgeList
              categories={category.map((cat) => cat.category_id)}
            />
          )}
        </HStack>
      </VStack>
    </Box>
  )
}

export default SearchResultCard
